import {Component, computed, Inject, inject, OnInit, signal, Signal, WritableSignal} from '@angular/core';
import {
  AlertController,
  IonContent,
  IonFab,
  IonFabButton,
  IonFabList,
  IonIcon,
  IonItem,
  IonList,
  IonNavLink,
  IonSkeletonText,
  NavController
} from "@ionic/angular/standalone";
import {TimelineEntry} from "@interfaces/timeline-entry";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {EventsService} from "@services/api";
import {HeaderImageComponent} from "@app/components/header-image/header-image.component";
import {DatePipe, DOCUMENT} from "@angular/common";
import {addIcons} from "ionicons";
import {calendar, checkmark, create, ellipsisVerticalOutline, trash} from "ionicons/icons";
import {ContainerComponent} from "@app/components/container/container.component";
import {HeaderComponent} from "@app/components/header/header.component";
import {NgIcon} from "@ng-icons/core";
import {Location} from "@app/interfaces";
import {MapSingleLocationComponent} from "@app/components/map-single-location/map-single-location.component";
import {NgxSeoService} from "@avivharuzi/ngx-seo";
import {
  TimelineEntryActionButtonsComponent
} from "@app/components/timeline-entry-action-buttons/timeline-entry-action-buttons.component";
import {firstValueFrom} from "rxjs";
import {EventsRepository} from "@app/repositories/events.repository";
import {AuthService} from "@app/services";
import {ToastService} from "@services/toast.service";
import {UpdateEventSheetService} from "@services/update-event-sheet.service";
import {br2nl} from "@app/utils/helpers";

@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.page.html',
  styleUrls: ['./event-detail.page.scss'],
  imports: [
    IonContent,
    HeaderImageComponent,
    DatePipe,
    IonIcon,
    RouterLink,
    ContainerComponent,
    HeaderComponent,
    NgIcon,
    IonNavLink,
    MapSingleLocationComponent,
    TimelineEntryActionButtonsComponent,
    IonSkeletonText,
    IonList,
    IonItem,
    IonFab,
    IonFabButton,
    IonFabList,

  ]
})
export class EventDetailPage implements OnInit {
  //event: TimelineEntry | undefined;
  event: WritableSignal<TimelineEntry> = signal({} as TimelineEntry);
  hasMapLocation: boolean = false;
  mainLocation: Location | undefined;
  isLoading: boolean = true;

  isOwner: Signal<boolean> = computed(() => {
    let isOwner = false;
    const event = this.event();
    if (event) {
      isOwner = this.authService.currentUser()?.id === event.owner.id || (this.authService.currentUser()?.ownsPlaceId(event.owner.id) ?? false);
    }
    return isOwner;
  });

  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected eventsService: EventsService = inject(EventsService);
  private eventsRepository: EventsRepository = inject(EventsRepository);
  private seoService: NgxSeoService = inject(NgxSeoService);
  private authService = inject(AuthService);
  private updateEventSheetService: UpdateEventSheetService = inject(UpdateEventSheetService);
  private alertController: AlertController = inject(AlertController);
  private toast: ToastService = inject(ToastService);
  private navCtrl: NavController = inject(NavController);

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.route.params.subscribe((params) => {
      console.log(`${this.constructor.name}.constructor route params`, params);

      if (params['placeSlug'] && params['eventSlug']) {
        this.loadEventBySlug(params['placeSlug'], params['eventSlug']);
        return;
      }

      if (!params['id']) {
        return;
      }

      const id = params['id'];
      this.loadEventById(id);
    });

    addIcons({calendar, create, checkmark, ellipsisVerticalOutline, trash});
  }

  ngOnInit() {
    console.log(`${this.constructor.name}.ngOnInit event`, this.event);
  }

  async loadEventById(id: string) {
    let data = await firstValueFrom(this.eventsRepository.filterById(id));
    console.log(`${this.constructor.name}.loadEventById`, data);
    if (!data) {
      data = await this.eventsService.getOneFromApi(id);
    }

    this.isLoading = false;
    if (!data) {
      return;
    }

    this.event.set(new TimelineEntry(data));

    window.location.href = this.event().getLink();
  }

  async loadEventBySlug(placeSlug: string, eventSlug: string) {
    console.log(`${this.constructor.name}.loadEventBySlug`, placeSlug, eventSlug);

    this.eventsRepository.events$.subscribe(async (data) => {
      let event = data.find(event => event.slug === eventSlug && event.owner.slug === placeSlug);

      if (!event) {
        event = await this.eventsService.getOneFromApiBySlug(placeSlug, eventSlug);
      }

      if (!event) {
        this.isLoading = false;
        return;
      }

      this.event.set(new TimelineEntry(event));

      this.setSeo(this.event());
      console.log(`${this.constructor.name}.loadEventBySlug event`, this.event);

      const mainLocation = this.event().locations?.find(location => location.type === 'main_location');
      if (mainLocation) {
        this.mainLocation = mainLocation;
        if (mainLocation?.lat && mainLocation?.lng) {
          this.hasMapLocation = true;
        }
      }

      this.isLoading = false;
    });
  }

  setSeo(event: TimelineEntry) {
    this.seoService.setSeo({
      title: event.title + ' | Events | Pfaffenhofen App',
      meta: {
        description: event.description?.toString().substring(0, 160),
        canonical: window.location.origin + event.getLink(),
      }
    });

    if (event.getMainImage() !== null) {
      const image = event.getMainImage()?.toString();
      this.seoService.setMetaImage(<string>image);
    }
  }

  async onClickUpdate() {
    const event = this.event().toEvent();
    console.log(`${this.constructor.name}.onClickUpdate`, event);
    if (event.description) {
      event.description = br2nl(event.description);
    }

    const data = Object.assign({}, event, {place_id: event.owner.id});
    await this.updateEventSheetService.open(data);
  }

  async onClickDelete() {
    const alert = await this.alertController.create({
      header: 'Event löschen',
      message: 'Möchtest du dieses Event wirklich löschen?',
      buttons: [
        {
          text: 'Abbrechen',
          role: 'cancel',
        },
        {
          text: 'Löschen',
          handler: () => this.deleteEvent()
        }
      ]
    });

    await alert.present();
  }

  private async deleteEvent() {
    const event = this.event().toEvent();
    console.log(`${this.constructor.name}.deleteEvent`, event);
    await this.eventsService.destroy(event).catch(async (error) => {
      console.error(`${this.constructor.name}.deleteEvent: error`, error);
      const alert = await this.alertController.create({
        message: 'Fehler beim Löschen des Events, bitte versuche es später erneut.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    });

    await this.toast.show('Event gelöscht');
    await this.navCtrl.navigateBack('/events');
  }
}
